import {
  downloadStatisticsTicketsReport,
  downloadTicketsReport,
  extractFileName,
  StatisticsSearchCriteria,
  TransportAssocEntity
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {useTranslation} from "react-i18next";
import {useApp} from "../providers/RegionProvider/RegionProvider"
import {useDebouncedState} from "@mantine/hooks";
import {BaseQueryApi} from "@reduxjs/toolkit/query";

export type UseDownloadTicketsReportProps = {
  baseQueryApi: BaseQueryApi
}

export type UseDownloadTicketsReportResult = {
  downloadTicketsReport: (tickets: TransportAssocEntity[]) => void
  downloadStatisticsTicketsReport: (criteria: StatisticsSearchCriteria) => void
}

export const useDownloadTicketsReport = (props: UseDownloadTicketsReportProps): UseDownloadTicketsReportResult => {
  const {baseQueryApi} = props

  const {regionId} = useApp()
  const {t} = useTranslation(["common"])

  // state
  const [_downloading, _setDownloading] = useDebouncedState(false, 500)

  useEffect(() => {
    _setDownloading(_downloading)
  }, [_downloading, _setDownloading])


  useEffect(() => {
    if (_downloading) {
      notifications.show({
        id: "useDownloadTicketsReport_updating",
        title: t("common.status.downloading", "Downloading") + " " + t("common.shared.report", "Report"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDownloadTicketsReport_updating")
    }
  }, [_downloading, t]);

  const _handleError = useCallback((rejected: any) => {
    notifications.show({
      color: "red",
      title: t("common.error", "Error"),
      message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
    })
  }, [t])

  const _handleResponse = useCallback((promise: Promise<Response>) => {
    promise.then((response) => {
      const fileName = extractFileName(response)
      response.headers.forEach((h) => console.debug(h))
      response.blob()
        .then((blob) => {
          _setDownloading(false)

          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName ?? `${new Date().getTime()}.xlsx`
          a.click();

          notifications.show({
            color: "green",
            title: t("common.shared.report", "Report") + " " + t("common.status.downloaded", "Downloaded"),
            message: ""
          });
        })
        .catch((rejected) => {
          _handleError(rejected)
        })
        .finally(() => {
          _setDownloading(false)
        })
    })
      .catch((rejected) =>
        _handleError(rejected)
      )
      .finally(() => {
        _setDownloading(false)
      })
  }, [_handleError, _setDownloading, t])

  const _downloadTicketsReport = useCallback((ttas: TransportAssocEntity[]) => {
    if (!regionId) {
      notifications.show({
        color: "red",
        title: t("common.error", "Error"),
        message: t<string>("common.error", "Error"),
      })
      return
    }

    _setDownloading(true)

    const promise = downloadTicketsReport({ttas: ttas}, baseQueryApi)
    _handleResponse(promise)
  }, [regionId, _setDownloading, baseQueryApi, _handleResponse, t])

  const _downloadStatisticsTicketsReport = useCallback((criteria: StatisticsSearchCriteria) => {
    if (!regionId) {
      notifications.show({
        color: "red",
        title: t("common.error", "Error"),
        message: t<string>("common.error", "Error"),
      })
      return
    }

    _setDownloading(true)

    const promise = downloadStatisticsTicketsReport(criteria, baseQueryApi)
    _handleResponse(promise)
  }, [regionId, _setDownloading, baseQueryApi, _handleResponse, t])

  return {
    downloadTicketsReport: _downloadTicketsReport,
    downloadStatisticsTicketsReport: _downloadStatisticsTicketsReport,
  }
}
